import * as React from 'react'
import { PageDefinition, PageType } from '../pageDefinition'
import { useNavigate, useParams } from 'react-router-dom'
import { useApiClient, useAuth, useCurrentAgency, useCurrentAgencyUsers, useFormFactor } from 'hooks'
import { PageTitleRightActions, SidePanelContent } from 'components'
import { Table, Button, Snackbar, Typography, ConfirmationModal, Tooltip } from 'cs-kevlar-react'
import { ColumnDef, MenuClick } from 'cs-kevlar-react/lib/es/table/base'
import { MapLayer, WebAdminUser } from '../../api'
import { IdentifiableModel } from 'cs-kevlar-react/lib/es/base'
import { canViewAgencyPage, matchIdOrDefault, serverDateToLocaleDateTimeString } from 'utils'
import { AddCircle, Edit } from 'cs-kevlar-react/lib/es/icons'
import { FilterOptions, PaginationOptions } from 'cs-kevlar-react/lib/es/table/Table'
import { makeStyles, Theme, createStyles } from 'cs-kevlar-react/lib/es/theming'
import { Box, IconButton } from 'cs-kevlar-react'
import { usePageViewAudit } from 'hooks/usePageViewAudit'
import { useCurrentAgencyCustomMapLayers } from 'hooks/useCurrentAgencyCustomMapLayers'
import { EditCustomMapLayer, SaveProgress } from 'components/customMapLayers/EditCustomMapLayer'
import { AlertColor, Grid } from '@mui/material'
import { useAgency } from 'hooks/useAgency'

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    iconButton: {
      // since this is hosted in the page title toolbar which has a right gutter
      // and the icon button itself has the same right padding, do a negative margin
      // so the button padding is used instead of the margin
      marginRight: -1 * parseInt(theme.spacing(8)),
      '& .MuiSvgIcon-root': {
        fontSize: '1.5rem',
      },
    },
    sharedCaption: {
      opacity: .54,
      marginTop: theme.spacing(-4)
    },
  }),
)

type CustomMapLayersProps = {
  customerAgencyId: string
  licensedAgencyId: string
  customMapLayerId?: string
}

type EditingCustomMapLayerState = MapLayer | null

type ValidMenuKey = 'Edit'

const FILTER_OPTIONS: Partial<FilterOptions> = {
  filterable: true,
  disableLiveFiltering: true,
  enableSuggestions: true,
  enableMultipleSearchTerms: true,
}

const PAGINATION_OPTIONS: Partial<PaginationOptions> = {
  singularAdjective: 'map layer',
  pluralAdjective: 'map layers',
}

type AssistedAgency = {
  customerAgencyId: string
  licensedAgencyId: string
}

const buildNavigationUrl = (
  customMapLayer: EditingCustomMapLayerState,
  assistedAgency: AssistedAgency | null,
) => {
  if (!assistedAgency) {
    if (customMapLayer === null) return `/customMapLayers`

    return `/customMapLayers/${customMapLayer.id}`
  }

  if (customMapLayer === null)
    return `/assist/customer/${assistedAgency.customerAgencyId}/agency/${assistedAgency.licensedAgencyId}/customMapLayers`

  return `/assist/customer/${assistedAgency.customerAgencyId}/agency/${assistedAgency.licensedAgencyId}/customMapLayers/${customMapLayer.id}`
}

const buildPageTitle = (user: WebAdminUser | null) => {
  if (!user?.licensedAgencyName) return 'Custom Map Layers'

  return `${user.licensedAgencyName} - Custom Map Layers`
}

const canEdit = (user: WebAdminUser | null, layer: MapLayer): boolean => {
  if (!user)
    return false

  if (user.isSupport)
    return true

  if (user.ownerAgencyId === layer.ownerAgency || user.authorizedLicensedAgencies?.includes(layer.ownerAgency) === true)
    return true

  return false
}

const CustomMapLayers: React.FunctionComponent = () => {

  const params = useParams<CustomMapLayersProps>()
  if(!params) return null

  const navigate = useNavigate()
  const { user: authUser, agencies } = useAuth()
  const formFactor = useFormFactor()
  const classes = useStyles({})
  const licensedAgencyId = params.licensedAgencyId ?? authUser?.licensedAgencyId

  usePageViewAudit(buildPageTitle(authUser), !!authUser)

  const {
    customMapLayers,
    customMapLayerLookup,
    loadCustomMapLayers,
    addCustomMapLayer,
    editCustomMapLayer,
    deleteCustomMapLayer,
  } = useCurrentAgencyCustomMapLayers(licensedAgencyId)

  const [isLoading, setIsLoading] = React.useState(false)
  const [snackbarMessageSeverity, setSnackbarMessageSeverity] = React.useState<AlertColor | undefined>(undefined)
  const [snackbarMessage, setSnackbarMessage] = React.useState<string | null>(null)
  const [snackbarMessageDetail, setSnackbarMessageDetail] = React.useState<string | null>(null)
  const [isViewingSnackbarMessageDetail, setIsViewingSnackbarMessageDetail] = React.useState<boolean>(false)

  const { initialize } = useCurrentAgencyUsers(licensedAgencyId)
  const { state: currentAgencyState } = useCurrentAgency(licensedAgencyId)
  const { getAgencies } = useAgency()

  const customerAgencyId =
    params.customerAgencyId ??
    currentAgencyState.agency?.data?.customerAgencyId

  const navigateToRoot = () => {
    if (
      authUser === null ||
      !authUser.customerAgencyId ||
      !authUser.licensedAgencyId
    )
      return navigate(buildNavigationUrl(null, null))

    navigate(
      buildNavigationUrl(null, {
        customerAgencyId: authUser.customerAgencyId,
        licensedAgencyId: authUser.licensedAgencyId,
      }),
    )
  }

  const selectCustomMapLayerForEditing = (customMapLayer: MapLayer | null) => {
    if (
      authUser === null ||
      !authUser.customerAgencyId ||
      !authUser.licensedAgencyId
    )
      return navigate(buildNavigationUrl(customMapLayer, null))

    navigate(
      buildNavigationUrl(customMapLayer, {
        customerAgencyId: authUser.customerAgencyId,
        licensedAgencyId: authUser.licensedAgencyId,
      }),
    )
  }

  const defaultCustomMapLayer: MapLayer = {
    id: 'NEW',
    ownerAgency: authUser?.licensedAgencyId ?? '',
    name: '',
    displayName: '',
    fileName: '',
    sharedAgencies: [],
    notes: ''
  }

  const columns: ColumnDef<
    MapLayer,
    ValidMenuKey
  >[] = React.useMemo(() => {
    return [
      {
        label: 'Map Layer Name',
        dataProperty: 'name' as keyof MapLayer,
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: MapLayer) => row.name,
        filterable: true,
        filterFunc: (row: MapLayer) => row.name,
        component: ({ row }) => {
          const ownerAgency = agencies.filter(x => x.licensedAgencyId == row.ownerAgency)[0]
          const isOwnerAgency = currentAgencyState.agency.data?.licensedAgencyId === row.ownerAgency
          const sharedAgencyCount = row.sharedAgencies?.length ?? 0

          return (
            <Grid>
              {row.name}

              <Box>
                <Tooltip
                  placement="right"
                  arrow
                  aria-label={decodeURIComponent(row.name)}
                  title={
                    <React.Fragment>
                      <Grid>
                        {row.sharedAgencies
                          .map(x => agencies.find(y => y.licensedAgencyId == x))
                          .map(x => x?.name)
                          .filter(x => !!x)
                          .sort()
                          .map(x => (<Box>{x}</Box>))
                        }
                      </Grid>
                    </React.Fragment>
                  }
                >
                  <span className={classes.sharedCaption}>
                    <Typography variant='caption'>
                      {isOwnerAgency && sharedAgencyCount > 0 &&
                        `Shared with ${sharedAgencyCount} ${(sharedAgencyCount == 1 ? "agency" : "agencies")}`
                      }

                      {!isOwnerAgency &&
                        `Shared from ${ownerAgency?.name ?? "N/A"}`
                      }
                    </Typography>
                    </span>
                </Tooltip>
              </Box>
            </Grid >
          )
        }
      },
      {
        label: 'App Layer Name',
        sortable: true,
        sortByDefault: true,
        sortValueFunc: (row: MapLayer) => row.displayName,
        filterable: true,
        dataProperty: 'displayName' as keyof MapLayer,
      },
      {
        label: 'File Name',
        sortable: true,
        sortByDefault: false,
        sortValueFunc: (row: MapLayer) => row.fileName,
        filterable: true,
        dataProperty: 'fileName' as keyof MapLayer,
      },
      {
        label: 'Upload Date',
        sortable: true,
        sortByDefault: false,
        sortValueFunc: (row: MapLayer) => row.uploadedOn,
        filterable: false,
        dataFunc: (p) => !p.uploadedOn ? "Pending" : serverDateToLocaleDateTimeString(p.uploadedOn),
      },
      {
        label: '',
        sortable: false,
        sortByDefault: false,
        filterable: false,
        menuGroups: [
          {
            options: [
              {
                label: 'Edit',
                key: 'Edit',
                icon: Edit,
                visible: (row: MapLayer) => canEdit(authUser, row)
              },
            ],
          },
        ],
      },
    ]
  }, [authUser, agencies])

  const editingCustomMapLayer = matchIdOrDefault(
    customMapLayerLookup,
    params.customMapLayerId,
    defaultCustomMapLayer,
  )

  const handleMenuClick = (key: string, row: MapLayer) => {
    if (key === 'Edit' && canEdit(authUser, row)) {
      selectCustomMapLayerForEditing(row)
      return
    }
  }

  const isEditing = editingCustomMapLayer !== null && editingCustomMapLayer.id !== 'NEW'
  const isAdding = editingCustomMapLayer !== null && editingCustomMapLayer.id === 'NEW'
  const [saveProgress, setSaveProgress] = React.useState<SaveProgress | undefined>(undefined)
  const onSaveProgress = (progress: ProgressEvent) => {
    const value = (progress.loaded / progress.total) * 100
    if (value < 1 || value >= 100)
      setSaveProgress('indeterminate')
    else
      setSaveProgress(value)
  }

  const onSave = async (customMapLayer: MapLayer, file: File | null) => {
    if (!customerAgencyId || !licensedAgencyId) {
      return
    }

    if (isAdding) {
      if (!file) {
        setSnackbarMessageSeverity('error')
        setSnackbarMessage('Cannot save map layer without a file.')
        return
      }
      
      customMapLayer.id = ''
    }

    setSaveProgress(1)
    await new Promise(resolve => setTimeout(resolve, 250))

    var result = undefined
    if (isAdding && file) {
      result = await addCustomMapLayer(customMapLayer, file, onSaveProgress)
    }
    else if (isEditing) {
      result = await editCustomMapLayer(customMapLayer, file ?? undefined, onSaveProgress)
    }

    setSaveProgress(undefined)

    if (result?.success) {
      setSnackbarMessage('Map layer saved successfully.')
      setSnackbarMessageDetail(null)
      setSnackbarMessageSeverity('success')
      navigateToRoot()
    }
    else {
      setSnackbarMessage('There was an error saving the map layer.')
      setSnackbarMessageDetail(result?.fileError ?? null)
      setSnackbarMessageSeverity('error')
    }
  }

  const onDelete = async () => {
    if (!customerAgencyId || editingCustomMapLayer === null) return

    await deleteCustomMapLayer(editingCustomMapLayer.id, navigateToRoot)
      .then((result) => {
        setSnackbarMessageSeverity('success')
        setSnackbarMessage('Map layer deleted successfully.')
      })
      .catch((error) => {
        setSnackbarMessageSeverity('error')
        setSnackbarMessage('There was an error deleting the map layer.')
      })
  }

  const loadData = async (force?: boolean) => {
    setIsLoading(true)
    await initialize()
    await getAgencies()
    await loadCustomMapLayers(force)
    setIsLoading(false)
  }

  React.useEffect(() => {
    loadData()
  }, [])

  return (
    <React.Fragment>
      <PageTitleRightActions>
        {!formFactor.isPhone && (
          <Button
            onClick={() => selectCustomMapLayerForEditing(defaultCustomMapLayer)}
            color="primary"
          >
            Add Custom Map Layer
          </Button>
        )}
        {formFactor.isPhone && (
          <IconButton
            onClick={() => selectCustomMapLayerForEditing(defaultCustomMapLayer)}
            color="primary"
            className={classes.iconButton}
            size="large">
            <AddCircle />
          </IconButton>
        )}
      </PageTitleRightActions>
      <Table
        data={customMapLayers}
        filterOptions={FILTER_OPTIONS}
        paginationOptions={PAGINATION_OPTIONS}
        columns={columns as ColumnDef<MapLayer, ValidMenuKey>[]}
        ariaLabel="Custom Map Layers for Licensed Agency"
        useDefaultRow
        isLoading={isLoading}
        handleMenuClick={
          handleMenuClick as MenuClick<IdentifiableModel, ValidMenuKey>
        }
        selectedId={editingCustomMapLayer?.id}
        uniqueIdentifier="CustomMapLayersList"
      />
      {(isEditing || isAdding) && (
        <SidePanelContent
          anchor="right"
          title={isAdding ? 'Add Custom Map Layer' : `Edit Custom Map Layer`}
          onClose={() => selectCustomMapLayerForEditing(null)}
          canClose={!saveProgress}
        >
          <EditCustomMapLayer
            customMapLayer={editingCustomMapLayer as MapLayer}
            canDelete={isEditing}
            saveText={isAdding ? 'Add' : 'Save'}
            onClose={() => selectCustomMapLayerForEditing(null)}
            onSave={onSave}
            onAdd={onSave}
            onDelete={onDelete}
            licensedAgencyId={licensedAgencyId}
            saveProgress={saveProgress}
          />
        </SidePanelContent>
      )}

      <Snackbar
        severity={snackbarMessageSeverity}
        open={snackbarMessage !== null}
        closeText={!!snackbarMessageDetail ? "Close" : undefined}
        autoHideDuration={!!snackbarMessageDetail ? null : 3000}
        onClose={() => {
          setSnackbarMessage(null)
          setSnackbarMessageDetail(null)
          setSnackbarMessageSeverity(undefined)
          setIsViewingSnackbarMessageDetail(false)
        }}
        actionButtonText={!!snackbarMessageDetail ? "View Details" : undefined}
        onActionButtonClicked={() => {
          setSnackbarMessage(null)
          setIsViewingSnackbarMessageDetail(true)
        }}
      >
        {snackbarMessage}
      </Snackbar>
      
      <ConfirmationModal
        title="Details"
        open={isViewingSnackbarMessageDetail}
        confirmText="Close"
        showCancelButton={false}
        onConfirm={() => setIsViewingSnackbarMessageDetail(false) }
        onCancel={() => setIsViewingSnackbarMessageDetail(false) }
        message={
          <Typography variant="body1">
            {snackbarMessageDetail}
          </Typography>
        }
        />

    </React.Fragment>
  );
}

export const CustomMapLayersPage: PageDefinition = {
  routes: (user) => {
    if (!user?.customerAgencyId) {
      return [
        '/customMapLayers/',
        '/customMapLayers/:customMapLayerId'
      ]
    }

    return [
      '/assist/customer/:customerAgencyId/agency/:licensedAgencyId/customMapLayers/',
      '/assist/customer/:customerAgencyId/agency/:licensedAgencyId/customMapLayers/:customMapLayerId'
    ]
  },
  type: [PageType.Agency],
  groupName: 'Configuration',
  sortOrder: () => 1,
  component: <CustomMapLayers />,
  navLinkText: 'Map Layers',
  pageTitle: buildPageTitle,
  canAccess: canViewAgencyPage,
  buildNavLink: (user) => {
    if (!user?.customerAgencyId || !user?.licensedAgencyId) return '/customMapLayers'

    return buildNavigationUrl(null, {
      customerAgencyId: user.customerAgencyId,
      licensedAgencyId: user.licensedAgencyId,
    })
  },
}
